// Récompenses du pass et objets sans palette : libellés et pictogrammes originaux, dessinés en SVG.
// Les objets de saison (emblèmes, bannières, effets…) n'ont pas encore de visuel en jeu : le pictogramme
// montre leur type, et les collections sont présentées en plan technique (silhouette au trait).
import { getClass } from '../../../shared/catalog/classes';
import { t, type Key } from '../i18n';
import { passItemLabel } from '../i18n/catalog';
import { CoinIcon, GemIcon, TokenIcon } from './icons';
import { TankImage } from './TankPreview';
import { BLUEPRINT } from './tankArt';

export type Reward =
  | { kind: 'coins'; amount: number }
  | { kind: 'gems'; amount: number }
  | { kind: 'token'; token: string; amount: number }
  | { kind: 'item'; item: string; label: string };

export type PassLevel = { level: number; free: Reward; premium: Reward };

export type ItemKind = 'emblem' | 'banner' | 'killfx' | 'deathfx' | 'shotfx' | 'trail' | 'frame' | 'collection' | 'item';

/** Type d'un objet de saison, lu dans son identifiant (s1:embleme-…, s1:effet-mort-…). */
export function itemKind(item: string): ItemKind {
  const s = item.replace(/^[a-z0-9]+:/, '');
  if (s.startsWith('embleme')) return 'emblem';
  if (s.startsWith('banniere')) return 'banner';
  if (s.startsWith('effet-elimination')) return 'killfx';
  if (s.startsWith('effet-mort')) return 'deathfx';
  if (s.startsWith('effet-tir')) return 'shotfx';
  if (s.startsWith('trainee')) return 'trail';
  if (s.startsWith('cadre')) return 'frame';
  if (s.startsWith('collection')) return 'collection';
  return 'item';
}

export function rewardLabel(r: Reward): string {
  if (r.kind === 'coins') return t('rewardCoins', { n: r.amount });
  if (r.kind === 'gems') return t('rewardGems', { n: r.amount });
  if (r.kind === 'token') return `${t(`token_${r.token}` as Key)}${r.amount > 1 ? ` ×${r.amount}` : ''}`;
  return passItemLabel(r.item, r.label);
}

export function rewardKindLabel(r: Reward): string {
  if (r.kind === 'coins') return t('rkind_coins');
  if (r.kind === 'gems') return t('rkind_gems');
  if (r.kind === 'token') return t('rkind_token');
  return t(`rkind_${itemKind(r.item)}` as Key);
}

/** Récompense « marquante » : objet, jeton ou gemmes (les pièces courantes restent discrètes sur la piste). */
export const isMajor = (r: Reward) => r.kind === 'item';

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Teintes des pictogrammes, tirées de l'identifiant : stables d'une visite à l'autre. */
const TINTS = ['#f2a23a', '#93a6ba', '#c8794a', '#7cc7a2', '#dcbb62', '#c98fb0'];
const BLUEPRINT_CLASSES = ['C01-F', 'C02-A', 'C04-B', 'C06-A', 'C09-B', 'C13-A'];

function ItemGlyph({ kind, tint, seed }: { kind: ItemKind; tint: string; seed: number }) {
  const stroke = { fill: 'none', stroke: tint, strokeWidth: 2.4, strokeLinecap: 'square' as const, strokeLinejoin: 'miter' as const };
  switch (kind) {
    case 'emblem': {
      const inner = seed % 3;
      return (
        <g>
          <path d="M24 5l16 6v12c0 9.5-6.8 16.6-16 20-9.2-3.4-16-10.5-16-20V11z" fill="#1d2127" stroke={tint} strokeWidth={2.4} />
          {inner === 0 && <path d="M24 14l2.8 6.4 7 .6-5.3 4.6 1.6 6.8L24 29l-6.1 3.4 1.6-6.8-5.3-4.6 7-.6z" fill={tint} />}
          {inner === 1 && (
            <>
              <circle cx="24" cy="23" r="7" {...stroke} />
              <circle cx="24" cy="23" r="2.6" fill={tint} />
            </>
          )}
          {inner === 2 && <path d="M24 13v20M15 18l18 10M33 18L15 28" {...stroke} />}
        </g>
      );
    }
    case 'banner':
      return (
        <g>
          <path d="M12 5v38" stroke="#7d8085" strokeWidth={2.4} />
          <path d="M14 7h22v26l-11-6-11 6z" fill="#1d2127" stroke={tint} strokeWidth={2.4} strokeLinejoin="miter" />
          <path d="M14 13h22" stroke={tint} strokeWidth={4} />
          <circle cx="25" cy="21" r="3.4" fill={tint} />
        </g>
      );
    case 'frame':
      return (
        <g>
          <rect x="7" y="7" width="34" height="34" fill="#1d2127" stroke={tint} strokeWidth={2.4} />
          <rect x="13" y="13" width="22" height="22" fill="none" stroke={tint} strokeWidth={1.4} opacity=".6" />
          {[
            [10, 10],
            [38, 10],
            [10, 38],
            [38, 38],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} cx={x} cy={y} r="2.4" fill={tint} />
          ))}
        </g>
      );
    case 'trail':
      return (
        <g>
          <path d="M6 30h20M10 23h18M14 16h14" {...stroke} opacity=".55" />
          <path d="M6 30h20" {...stroke} />
          <circle cx="34" cy="23" r="7" fill="#1d2127" stroke={tint} strokeWidth={2.4} />
        </g>
      );
    case 'killfx':
      return (
        <g>
          {Array.from({ length: 8 }, (_, i) => {
            const a = (i / 8) * Math.PI * 2 + (seed % 7) * 0.1;
            return <path key={i} d={`M${24 + Math.cos(a) * 7} ${24 + Math.sin(a) * 7}L${24 + Math.cos(a) * 18} ${24 + Math.sin(a) * 18}`} {...stroke} />;
          })}
          <circle cx="24" cy="24" r="4" fill={tint} />
        </g>
      );
    case 'deathfx':
      return (
        <g>
          <path d="M24 10l6 10-6 4-6-4z" fill={tint} />
          <path d="M12 26l9 1-3 9-8-4z" fill={tint} opacity=".8" />
          <path d="M36 26l-9 1 3 9 8-4z" fill={tint} opacity=".65" />
          <path d="M22 30h4l1 8h-6z" fill={tint} opacity=".5" />
        </g>
      );
    case 'shotfx':
      return (
        <g>
          <rect x="5" y="20" width="16" height="8" fill="#2a2f36" stroke="#7d8085" strokeWidth={2} />
          <path d="M22 24l10-9 2 6 9 3-9 3-2 6z" fill={tint} />
          <circle cx="40" cy="24" r="3" fill={tint} />
        </g>
      );
    default:
      return <rect x="10" y="10" width="28" height="28" fill="#1d2127" stroke={tint} strokeWidth={2.4} />;
  }
}

/** Pictogramme d'une récompense, à la taille demandée (pixels CSS). */
export function RewardArt({ r, size = 48 }: { r: Reward; size?: number }) {
  if (r.kind === 'coins') {
    const n = r.amount >= 1000 ? 3 : r.amount >= 300 ? 2 : 1;
    return (
      <span className="reward-art reward-coins" style={{ width: size, height: size }} aria-hidden>
        {Array.from({ length: n }, (_, i) => (
          <span key={i} className="reward-stack" style={{ transform: `translate(${(i - (n - 1) / 2) * size * 0.2}px, ${-i * size * 0.08}px)` }}>
            <CoinIcon size={size * 0.62} />
          </span>
        ))}
      </span>
    );
  }
  if (r.kind === 'gems') {
    const n = r.amount >= 500 ? 3 : r.amount >= 100 ? 2 : 1;
    return (
      <span className="reward-art reward-gems" style={{ width: size, height: size }} aria-hidden>
        {Array.from({ length: n }, (_, i) => (
          <span key={i} className="reward-stack" style={{ transform: `translate(${(i - (n - 1) / 2) * size * 0.24}px, ${(i % 2) * size * 0.06}px)` }}>
            <GemIcon size={size * (n === 1 ? 0.66 : 0.52)} />
          </span>
        ))}
      </span>
    );
  }
  if (r.kind === 'token')
    return (
      <span className="reward-art reward-token" style={{ width: size, height: size }} aria-hidden>
        <TokenIcon token={r.token} size={size * 0.78} />
      </span>
    );
  return <ItemArt kind={itemKind(r.item)} id={r.item} size={size} />;
}

/** Pictogramme d'un objet sans palette (emblème, bannière, effet, traînée, cadre) ou plan d'une collection. */
export function ItemArt({ kind, id, size = 48 }: { kind: ItemKind; id: string; size?: number }) {
  const h = hash(id);
  if (kind === 'collection')
    return (
      <span className="reward-art reward-collection" style={{ width: size, height: size }} aria-hidden>
        <TankImage cls={getClass(BLUEPRINT_CLASSES[h % BLUEPRINT_CLASSES.length])} look={{ palette: BLUEPRINT }} size={Math.round(size * 0.92)} />
      </span>
    );
  return (
    <span className="reward-art" style={{ width: size, height: size }} aria-hidden>
      <svg viewBox="0 0 48 48" width={size} height={size} focusable="false">
        <ItemGlyph kind={kind} tint={TINTS[h % TINTS.length]} seed={h} />
      </svg>
    </span>
  );
}
